import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {Avatar} from '@forecast-it/design-system';
import type {WatchersList_ticket$key} from './__generated__/WatchersList_ticket.graphql';
import type {ticketingPerson_person$key} from '../../../graphql/fragments/__generated__/ticketingPerson_person.graphql';
import {ticketingPersonFragment, personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {useWatchTicket} from '../hooks/useWatchTicket';

const WatchersListFragment = graphql`
	fragment WatchersList_ticket on Ticket {
		id
		isWatching
		watcherCount
		watchers {
			id
			person {
				...ticketingPerson_person
			}
		}
	}
`;

interface Props {
	ticket: WatchersList_ticket$key;
}

const Section = styled.section`
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 20px;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;
const Header = styled.div`
	margin-bottom: 8px;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const Title = styled.span`
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 600;
	color: #0f172a;
`;
const Count = styled.span`
	margin-left: 6px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #64748b;
`;
const ToggleButton = styled.button<{$watching: boolean}>`
	border-radius: 6px;
	padding: 4px 10px;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	cursor: pointer;
	border: 1px solid ${p => (p.$watching ? '#bfdbfe' : '#e2e8f0')};
	background: ${p => (p.$watching ? '#eff6ff' : 'transparent')};
	color: ${p => (p.$watching ? '#1d4ed8' : '#475569')};
	&:hover {
		background: ${p => (p.$watching ? '#dbeafe' : '#f8fafc')};
	}
`;
const Description = styled.p`
	${p => p.theme.sys.font?.detail?.['1']}
	color: #94a3b8;
	margin: 0;
`;
const ListWrap = styled.div`
	margin-top: 12px;
	min-height: 2rem;
`;
const List = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;
const Row = styled.li`
	display: flex;
	align-items: center;
	gap: 10px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #334155;
`;
const Name = styled.span`
	font-weight: 500;
`;

function WatcherRow({person}: {person: ticketingPerson_person$key | null | undefined}) {
	const data = useFragment(ticketingPersonFragment, person ?? null);
	const name = data ? personDisplayName(data) : 'Unknown';
	return (
		<Row>
			<Avatar size="s" name={name} />
			<Name>{name}</Name>
		</Row>
	);
}

export function WatchersList({ticket}: Props) {
	const data = useFragment<WatchersList_ticket$key>(WatchersListFragment, ticket);
	const {watch, unwatch, inFlight} = useWatchTicket();

	const handleToggle = () => {
		if (data.isWatching) {
			unwatch(data.id);
		} else {
			watch(data.id);
		}
	};

	return (
		<Section>
			<Header>
				<Title>
					Watchers
					{data.watcherCount > 0 && <Count>{data.watcherCount}</Count>}
				</Title>
				<ToggleButton type="button" onClick={handleToggle} disabled={inFlight} $watching={data.isWatching}>
					{inFlight ? '…' : data.isWatching ? 'Stop Watching' : 'Watch'}
				</ToggleButton>
			</Header>
			<Description>Watchers get notified when status, assignee, priority, or comments change.</Description>
			<ListWrap>
				{data.watchers.length > 0 && (
					<List>
						{data.watchers.map(w => (
							<WatcherRow key={w.id} person={w.person} />
						))}
					</List>
				)}
			</ListWrap>
		</Section>
	);
}
